import React, { useMemo } from 'react';
import Icon from 'components/Utils/NewIcon';
import PatientInfo from 'components/Patient/PatientInfo';
import AppointmentList from 'components/HealthRecords/Appointments/AppointmentList';
import PrescriptionsList from 'components/HealthRecords/Prescriptions/PrescriptionsList';
import ImmunizationsList from 'components/HealthRecords/Immunizations/ImmunizationsList';
import DocumentListWithDetails from 'components/HealthRecords/Documents/DocumentListWithDetails';
import AccountsList from 'components/Patient/AccountsList';
import DocumentUtils from 'services/DocumentUtils';
import AdminTopBar from 'components/Chat/TopBars/AdminTopBar';
import './style.scss';
import ChatShortcuts from 'components/Chat/ChatShortcuts';
import PatientTasks from 'scenes/HomeStaff/PatientDirectory/PatientTasks';
import { TasksUnreadBadge } from 'scenes/HomeStaff/PatientDirectory/PatientProfile/view';
import DefaultButton from 'components/Utils/NewDefaultButton';
import StaffSearchModal from 'components/Modals/StaffSearchModal';
import DefaultModal from 'components/Utils/DefaultModal';
import PatientCommunication from 'components/Chat/PatientCommunication';
import PatientFaxes from 'scenes/HomeStaff/PatientDirectory/PatientFaxes';
import PatientBilling from 'scenes/HomeStaff/PatientDirectory/PatientBilling';
import { useAppSelector } from 'careand-redux/store';
import ChatRoomWindow from 'components/Chat/ChatWindow/ChatRoomWindow';
import i18n from 'services/i18n';

const AdminChatView = ({
  activeLinkedAccount,
  bubble,
  patients,
  visible,
  activePatient,
  onClose,
  setChatMessage,
  handleIconClick,
  archiveChat,
  setActiveLinkedAccount,
  transferring,
  modal,
  setModal,
  transferStaff,
}) => {
  let patient = patients[bubble.patient_id];
  let mainPatient = patient;
  let linkedAccountId = activeLinkedAccount[bubble.id];

  if (!linkedAccountId) linkedAccountId = mainPatient.id;

  if (patient === undefined) return null;

  let activeComponent = (
    <ActiveComponent
      setMessage={setChatMessage}
      mainPatient={mainPatient}
      activePatient={activePatient}
      linkedAccountId={linkedAccountId}
      setActiveLinkedAccount={setActiveLinkedAccount}
      bubble={bubble}
    />
  );

  let extensionClassName = 'extension';
  if (activeComponent === undefined) extensionClassName += ' closed';

  let className = 'row patient-chat';

  if (visible !== true) className += ' hide';

  return (
    <div className={className} key={mainPatient.id}>
      {modal && (
        <DefaultModal
          key="physician-search-modal"
          shouldCloseOnEsc
          content={
            <StaffSearchModal
              onSelect={(staff) => transferStaff(staff)}
              title={`Select ${i18n.withIndefiniteArticle.concierge}`}
              ignorePhysicians={true}
              hideInactive={true}
            />
          }
          isOpen={modal}
          onRequestClose={() => setModal(false)}
        />
      )}
      <div className="under-layer" onClick={onClose} />
      <ChatRoomWindow
        key={mainPatient.id}
        bubble={bubble}
        patient={mainPatient}
        onClose={onClose}
        archiveChat={archiveChat}
        allowAssistant={true}
        renderTopBar={() => (
          <AdminTopBar
            patient={mainPatient}
            bubble={bubble}
            onClose={onClose}
            archive={archiveChat}
          >
            <div className="row justify-center">
              <DefaultButton
                id="transfer-button"
                name="Transfer to Staff"
                className="blue"
                onClick={() => setModal(true)}
                style={{ width: '160px', position: 'relative', top: '10px' }}
                icon={['fas', 'share']}
                loading={transferring}
              />
            </div>
          </AdminTopBar>
        )}
      />
      <ChatListIcons
        bubble={bubble}
        handleIconClick={handleIconClick}
        activePatient={activePatient}
      />
      <div className={extensionClassName}>{activeComponent}</div>
    </div>
  );
};

export const ChatListIcons = ({ bubble, handleIconClick, activePatient }) => (
  <ul className="icon-list shadow-300">
    {Object.keys(CHAT_ICONS).map((tab) => {
      let show = !!activePatient || tab === 'shortcuts';
      return (
        !!show && (
          <React.Fragment key={tab}>
            <li className="clickable" onClick={() => handleIconClick(tab)}>
              <Icon
                width="24px"
                src={CHAT_ICONS[tab]}
                showTooltip={true}
                tooltipPlace="right"
                className={tab === bubble.tab ? 'blue' : 'dark-grey'}
              />
              {CHAT_ICONS[tab] === CHAT_ICONS.tasks && <TasksUnreadBadge patient={activePatient} />}
            </li>
          </React.Fragment>
        )
      );
    })}
  </ul>
);

const PaddedList = (props) => (
  <List {...props}>
    <div className="padded-box">{props.children}</div>
  </List>
);

const List = (props) => (
  <div className="full-height-panel underside-right-panel scroll-list" style={props.style}>
    {props.children}
  </div>
);

const DEFAULT_OBJECT = {};

export const ActiveComponent = ({
  mainPatient,
  activePatient,
  linkedAccountId,
  sendMessage,
  setActiveLinkedAccount,
  bubble,
  setMessage,
}) => {
  const patientHealthRecords =
    useAppSelector((state) => state.healthRecords.byId[activePatient?.id]) || DEFAULT_OBJECT;

  const referrals = useMemo(
    () =>
      DocumentUtils.filter(patientHealthRecords.documents, DocumentUtils.DOCUMENT_TYPE.referral),
    [patientHealthRecords],
  );
  const diagnostics = useMemo(
    () =>
      DocumentUtils.filter(patientHealthRecords.documents, DocumentUtils.DOCUMENT_TYPE.diagnostic),
    [patientHealthRecords],
  );
  const others = useMemo(
    () => DocumentUtils.filterOthers(patientHealthRecords.documents, true),
    [patientHealthRecords],
  );

  return (
    <>
      {bubble.tab === 'linked' && (
        <List style={{ width: '390px' }}>
          <AccountsList
            patient={mainPatient}
            activeId={linkedAccountId}
            onClick={setActiveLinkedAccount}
            allowUnlink={false}
          />
        </List>
      )}
      {!!activePatient && (
        <>
          {bubble.tab === 'user' && (
            <List style={{ width: '390px' }}>
              <PatientInfo patient={activePatient} sendMessage={sendMessage} />
            </List>
          )}
          {bubble.tab === 'billing' && (
            <PaddedList style={{ width: '390px' }}>
              <PatientBilling patient={activePatient} />
            </PaddedList>
          )}
          {bubble.tab === 'appointments' && (
            <AppointmentList
              patient={activePatient}
              bookedBy={mainPatient}
              sendMessage={sendMessage}
              allowNewAppointment={true}
              allowEdit={true}
              openAppointmentId={bubble.tabPayload}
              showCalendar={false}
            />
          )}
          {bubble.tab === 'immunizations' && (
            <ImmunizationsList
              key={activePatient?.id}
              title="Immunizations"
              patient={activePatient}
              immunizations={patientHealthRecords?.immunizations || []}
            />
          )}
          {bubble.tab === 'prescriptions' && (
            <PrescriptionsList
              title="Prescriptions"
              patient={activePatient}
              prescriptions={patientHealthRecords?.prescriptions || []}
              refills={patientHealthRecords?.refills || []}
            />
          )}
          {bubble.tab === 'tasks' && <PatientTasks patient={activePatient} />}
          {bubble.tab === 'referrals' && (
            <DocumentListWithDetails
              key={'referrals-' + activePatient?.id}
              allowExpand={true}
              title="Referrals"
              patient={activePatient}
              disableEdit={false}
              documents={referrals}
            />
          )}
          {bubble.tab === 'diagnostics' && (
            <DocumentListWithDetails
              key={'diagnostics-' + activePatient?.id}
              title="Diagnostics"
              patient={activePatient}
              disableEdit={false}
              documents={diagnostics}
            />
          )}
          {bubble.tab === 'documents' && (
            <DocumentListWithDetails
              key={'documents-' + activePatient?.id}
              title="Documents"
              patient={activePatient}
              disableEdit={false}
              documents={others}
            />
          )}
          {bubble.tab === 'faxes' && (
            <PatientFaxes key={'faxes-' + activePatient?.id} patient={activePatient} />
          )}
          {bubble.tab === 'communications' && <PatientCommunication patient={activePatient} />}
        </>
      )}
      {bubble.tab === 'shortcuts' && (
        <ChatShortcuts onSelect={(shortcut) => setMessage(shortcut.value)} />
      )}
    </>
  );
};

export const CHAT_ICONS = {
  user: 'user',
  linked: 'family',
  appointments: 'calendar-clock',
  billing: 'wallet',
  diagnostics: 'chart-line',
  referrals: 'user-doctor',
  prescriptions: 'prescription',
  documents: 'files-medical',
  immunizations: 'vial-virus',
  tasks: 'circle-check',
  faxes: 'fax',
  communications: 'inbox-full',
  shortcuts: 'message-captions',
};

export default AdminChatView;
